import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ErrorBoundary from '../shared/ErrorBoundary';
import MessageList from '../shared/MessageList';
import JSONCollapsibleViewer from '../shared/JSONCollapsibleViewer';
import RawValueDisplay from '../shared/RawValueDisplay';
import ToolSchemasChevron from '../shared/ToolSchemasChevron';
import CopyButton from '../../../../components/CopyButton';
import {
  extractLLMTools,
  extractPromptTemplate,
  extractPrompts,
  extractInvocationParameters,
  extractInputOutputMessages
} from '../../../../utils/openinference';
import { typography, palette, withOpacity } from '../../../../utils/designTokens';
import { logger } from '../../../../utils/logger';

/**
 * ConfigurationTab - Displays LLM configuration details
 * Only used by LLM span kind (Tools, Prompt Template, Prompts, Invocation Parameters)
 *
 * @param {Object} span - Full span object
 */
const CollapsibleSection = ({ title, defaultExpanded = false, nested = false, headerActions = null, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div>
      <div
        className="w-full flex items-center justify-between rounded transition-colors hover:bg-opacity-5 border-t"
        style={{
          backgroundColor: palette.neutrals.panelSecondary,
          borderColor: palette.borders.subtle,
          minHeight: 44,
          height: 44,
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center flex-1 p-2"
          style={{ paddingLeft: nested ? 40 : 20 }}
        >
          <div className="flex items-center">
            {expanded ? (
              <ChevronDown size={14} style={{ color: palette.text.muted }} />
            ) : (
              <ChevronRight size={14} style={{ color: palette.text.muted }} />
            )}
            <span className={`${typography.size.metadata} font-semibold text-text-muted ${nested ? '' : 'uppercase'}`}>
              {title}
            </span>
          </div>
        </button>
        <div className="flex items-center pr-2" onClick={(e) => e.stopPropagation()}>
          {headerActions}
        </div>
      </div>
      {expanded && (
        <div className="mt-2" style={{ paddingLeft: nested ? 40 : 20 }}>
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to detect the starting message index from raw attributes.
 */
const detectMessageStartIndex = (attributes) => {
  if (!attributes) return null;
  const attrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
  const llmAttrs = attrs.llm || {};
  const messages = llmAttrs.input_messages;

  if (Array.isArray(messages) && messages.length > 0) {
    const indices = messages
      .map((_, idx) => idx)
      .filter(idx => messages[idx] && messages[idx].message);
    return indices.length > 0 ? Math.min(...indices) : null;
  }

  const prefix = 'llm.input_messages';
  const messageKeys = Object.keys(attrs).filter(key => key.startsWith(prefix + '.'));
  if (messageKeys.length === 0) return null;

  const indices = [...new Set(
    messageKeys
      .map(key => {
        const match = key.match(/^llm\.input_messages\.(\d+)\./);
        return match ? parseInt(match[1]) : null;
      })
      .filter(idx => idx !== null)
  )];
  return indices.length > 0 ? Math.min(...indices) : null;
};

/**
 * Find sibling ChatPromptTemplate span and extract prompt template.
 */
const findSiblingPromptTemplate = (span, spans) => {
  if (!span || !spans || spans.length === 0) return null;
  const parentSpanId = span.parent_span_id;
  if (!parentSpanId) return null;

  const siblingPromptSpan = spans.find(s =>
    s.parent_span_id === parentSpanId &&
    s.span_id !== span.span_id &&
    (s.span_name === 'ChatPromptTemplate' || s.name === 'ChatPromptTemplate')
  );
  if (!siblingPromptSpan) return null;

  try {
    const attrs = typeof siblingPromptSpan.attributes === 'string'
      ? JSON.parse(siblingPromptSpan.attributes)
      : siblingPromptSpan.attributes;
    if (attrs?.llm?.prompt_template?.template) return attrs.llm.prompt_template.template;
    if (attrs?.['llm.prompt_template.template']) return attrs['llm.prompt_template.template'];
  } catch (e) {
    logger.error('Failed to extract prompt template from sibling span:', e);
  }
  return null;
};

const ConfigurationTab = ({ span, spans = [], spanKind, drilldownApi = null }) => {
  if (!span) {
    throw new Error('ConfigurationTab requires span prop');
  }

  logger.debug('ConfigurationTab rendering:', { spanId: span.span_id });

  const isV2Span = span.__v2Span === true;

  const [drilldownToolSchema, setDrilldownToolSchema] = useState(null);
  const [drilldownToolSchemaError, setDrilldownToolSchemaError] = useState(null);
  useEffect(() => {
    setDrilldownToolSchema(null);
    setDrilldownToolSchemaError(null);
    if (!drilldownApi || spanKind !== 'llm') return;
    let cancelled = false;
    drilldownApi.fetchToolSchema()
      .then(data => { if (!cancelled) setDrilldownToolSchema(data); })
      .catch(err => { if (!cancelled) setDrilldownToolSchemaError(err?.message || 'Failed to load tool schema'); });
    return () => { cancelled = true; };
  }, [span.span_id, drilldownApi]); // eslint-disable-line

  // Extract system prompt for LLM spans
  let systemPromptContent = null;
  let systemPromptCopyText = null;
  if (spanKind === 'llm') {
    if (isV2Span) {
      if (span.system_prompt) {
        systemPromptCopyText = span.system_prompt;
        systemPromptContent = <MessageList messages={[{ role: 'system', content: span.system_prompt }]} type="input" />;
      }
    } else {
      try {
        const { input: inputMessages } = extractInputOutputMessages(span.attributes);
        const systemMessages = inputMessages ? inputMessages.filter(msg => msg.role === 'system') : [];
        const hasOtherMessages = inputMessages && inputMessages.some(msg => msg.role !== 'system');

        if (systemMessages.length > 0) {
          systemPromptCopyText = systemMessages
            .map((msg) => {
              if (!msg || msg.content === null || msg.content === undefined) return '';
              if (typeof msg.content === 'string') return msg.content;
              try {
                return JSON.stringify(msg.content, null, 2);
              } catch (_err) {
                return String(msg.content);
              }
            })
            .filter(Boolean)
            .join('\n\n');
          systemPromptContent = <MessageList messages={systemMessages} type="input" />;
        } else if (hasOtherMessages) {
          const startIndex = detectMessageStartIndex(span.attributes);
          if (startIndex !== null && startIndex > 0) {
            const siblingPromptTemplate = findSiblingPromptTemplate(span, spans);
            if (siblingPromptTemplate) {
              systemPromptCopyText = siblingPromptTemplate;
              systemPromptContent = (
                <div>
                  <div className="text-xs text-text-muted mb-2 italic">
                    System prompt extracted from sibling ChatPromptTemplate span (messages truncated at index {startIndex})
                  </div>
                  <div
                    className="p-3 rounded border font-mono text-sm whitespace-pre-wrap"
                    style={{
                      backgroundColor: palette.neutrals.canvas,
                      borderColor: palette.borders.subtle,
                      color: palette.text.primary
                    }}
                  >
                    {siblingPromptTemplate}
                  </div>
                </div>
              );
            } else {
              const truncatedIndices = startIndex === 1
                ? `index 0 has`
                : `indexes 0-${startIndex - 1} have`;
              systemPromptContent = (
                <div className="p-4 text-sm text-text-muted italic">
                  No system prompt found. Message {truncatedIndices} been truncated.
                </div>
              );
            }
          } else {
            systemPromptContent = (
              <div className="p-4 text-sm text-text-muted italic">
                No system prompt found.
              </div>
            );
          }
        }
      } catch (e) {
        logger.error('Failed to extract system prompt:', e);
      }
    }
  }

  let tools = [];
  let promptTemplate = null;
  let prompts = [];
  let invocationParams = null;

  if (isV2Span) {
    if (drilldownToolSchema?.tool_schemas?.length > 0) {
      tools = drilldownToolSchema.tool_schemas;
    }
    if (span.invocation_params) {
      try {
        const parsed = typeof span.invocation_params === 'string'
          ? JSON.parse(span.invocation_params)
          : span.invocation_params;
        invocationParams = parsed && Object.keys(parsed).length > 0 ? parsed : null;
      } catch (e) {
        logger.error('Failed to parse invocation_params:', e);
      }
    }
    if (span.prompt_template) {
      try {
        let variables = null;
        if (span.prompt_template_variables) {
          const pv = typeof span.prompt_template_variables === 'string'
            ? JSON.parse(span.prompt_template_variables)
            : span.prompt_template_variables;
          variables = pv && Object.keys(pv).length > 0 ? pv : null;
        }
        promptTemplate = { template: span.prompt_template, variables };
      } catch (e) {
        logger.error('Failed to parse prompt_template_variables:', e);
        promptTemplate = { template: span.prompt_template, variables: null };
      }
    }
    if (span.prompts) {
      try {
        const parsed = typeof span.prompts === 'string'
          ? JSON.parse(span.prompts)
          : span.prompts;
        prompts = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        logger.error('Failed to parse prompts:', e);
      }
    }
  } else {
    try {
      tools = extractLLMTools(span.attributes);
    } catch (e) {
      logger.error('Failed to extract LLM tools:', e);
    }

    try {
      promptTemplate = extractPromptTemplate(span.attributes);
    } catch (e) {
      logger.error('Failed to extract prompt template:', e);
    }

    try {
      prompts = extractPrompts(span.attributes);
    } catch (e) {
      logger.error('Failed to extract prompts:', e);
    }

    try {
      invocationParams = extractInvocationParameters(span.attributes);
    } catch (e) {
      logger.error('Failed to extract invocation parameters:', e);
    }
  }

  const hasContent = systemPromptContent || tools.length > 0 || promptTemplate || prompts.length > 0 || invocationParams;

  if (!hasContent) {
    return (
      <div className="text-sm text-text-muted">
        {isV2Span && drilldownToolSchemaError
          ? `Failed to load tool schemas: ${drilldownToolSchemaError}`
          : 'No configuration data available'}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="space-y-0">
        {/* System Prompt */}
        {systemPromptContent && (
          <CollapsibleSection
            title="System Prompt"
            defaultExpanded={false}
            headerActions={systemPromptCopyText ? <CopyButton text={systemPromptCopyText} size="xs" label="Copy system prompt" /> : null}
          >
            {systemPromptContent}
          </CollapsibleSection>
        )}

        {/* Tools */}
        {tools.length > 0 && (
          <CollapsibleSection title={`Tools (${tools.length})`} defaultExpanded={true}>
            <ToolSchemasChevron tools={tools} />
          </CollapsibleSection>
        )}
        {isV2Span && drilldownToolSchema?.tool_schemas_truncated && (
          <div className="px-3 py-2 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.panelSecondary, color: palette.text.muted }}>
            Tool schema list exceeds the display limit and was omitted.
          </div>
        )}
        {isV2Span && drilldownToolSchemaError && tools.length === 0 && (
          <div className="p-3 text-sm rounded border" style={{ color: palette.status.danger, borderColor: withOpacity(palette.status.danger, 25), backgroundColor: withOpacity(palette.status.danger, 6) }}>
            Failed to load tool schemas: {drilldownToolSchemaError}
          </div>
        )}

        {/* Prompt Template */}
        {promptTemplate && (
          <CollapsibleSection
            title="Prompt Template"
            defaultExpanded={false}
            headerActions={promptTemplate?.template ? <CopyButton text={promptTemplate.template} size="xs" label="Copy prompt template" /> : null}
          >
            <div className="space-y-2">
              <RawValueDisplay value={promptTemplate.template} label="Template" />
              {promptTemplate.variables && Object.keys(promptTemplate.variables).length > 0 && (
                <JSONCollapsibleViewer data={promptTemplate.variables} label="Variables" />
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Prompts Array */}
        {prompts.length > 0 && (
          <CollapsibleSection title={`Prompts (${prompts.length})`} defaultExpanded={false}>
            <div className="space-y-2">
              {prompts.map((prompt, idx) => (
                <RawValueDisplay key={idx} value={prompt} label={`Prompt ${idx + 1}`} />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Invocation Parameters */}
        {invocationParams && (
          <CollapsibleSection title="Invocation Parameters" defaultExpanded={false}>
            <JSONCollapsibleViewer data={invocationParams} label="Parameters" />
          </CollapsibleSection>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ConfigurationTab;
